import React from 'react';
import type { DayOverview, DayStatus } from '../slices/overviewSlice';

const WEEKDAY_HEADERS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

interface StatusMeta {
  label: string;
  bg: string;
  color: string;
  border: string;
}

export const STATUS_META: Record<DayStatus, StatusMeta> = {
  filled: { label: 'Erfasst', bg: '#d1e7dd', color: '#0f5132', border: '#a3cfbb' },
  missing: { label: 'Fehlt', bg: '#f8d7da', color: '#842029', border: '#f1aeb5' },
  off: { label: 'Frei', bg: '#f1f3f5', color: '#868e96', border: '#e9ecef' },
  holiday: { label: 'Feiertag', bg: '#cfe2ff', color: '#084298', border: '#9ec5fe' },
  vacation: { label: 'Urlaub', bg: '#cff4fc', color: '#055160', border: '#9eeaf9' },
  sick: { label: 'Krank', bg: '#fff3cd', color: '#664d03', border: '#ffe69c' },
  future: { label: '', bg: '#ffffff', color: '#adb5bd', border: '#e9ecef' },
};

const columnForDate = (dateStr: string) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const jsDay = new Date(y, m - 1, d).getDay(); // 0=So
  return (jsDay + 6) % 7; // Montag = 0
};

interface Props {
  days: DayOverview[];
  selectedDates: string[];
  todayKey: string;
  isAdmin?: boolean;
  onDayClick: (day: DayOverview, shiftKey: boolean) => void;
  onDayDoubleClick?: (day: DayOverview) => void;
}

const MonthGrid: React.FC<Props> = ({ days, selectedDates, todayKey, isAdmin, onDayClick, onDayDoubleClick }) => {
  if (days.length === 0) return null;

  const selected = new Set(selectedDates);
  const leadingBlanks = columnForDate(days[0].date);

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 6,
          marginBottom: 6,
        }}
      >
        {WEEKDAY_HEADERS.map((h) => (
          <div key={h} className="text-center text-muted fw-semibold" style={{ fontSize: 13 }}>
            {h}
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
        {Array.from({ length: leadingBlanks }).map((_, i) => (
          <div key={`blank-${i}`} />
        ))}
        {days.map((day) => {
          const effectiveStatus = (!isAdmin && day.status === 'missing') ? 'future' : day.status;
          const meta = STATUS_META[effectiveStatus];
          const dayNum = Number(day.date.split('-')[2]);
          const isToday = day.date === todayKey;
          const isSelected = selected.has(day.date);
          return (
            <button
              key={day.date}
              type="button"
              onClick={(e) => onDayClick(day, e.shiftKey)}
              onDoubleClick={() => onDayDoubleClick?.(day)}
              title={day.planned ? `${meta.label} · geplant — Doppelklick zum Bearbeiten` : meta.label}
              style={{
                background: meta.bg,
                color: meta.color,
                borderTop: `1px solid ${isSelected ? '#0d6efd' : meta.border}`,
                borderRight: `1px solid ${isSelected ? '#0d6efd' : meta.border}`,
                borderBottom: `1px solid ${isSelected ? '#0d6efd' : meta.border}`,
                borderLeft: day.planned && day.status !== 'vacation' && day.status !== 'holiday' && day.status !== 'sick' ? '4px solid #0d6efd' : `1px solid ${isSelected ? '#0d6efd' : meta.border}`,
                boxShadow: isSelected ? '0 0 0 2px rgba(13,110,253,.45)' : 'none',
                borderRadius: 8,
                padding: '8px 6px',
                minHeight: 74,
                textAlign: 'left',
                cursor: 'pointer',
                position: 'relative',
                transition: 'box-shadow .1s',
              }}
            >
              <div className="d-flex justify-content-between align-items-start">
                <span style={{ fontWeight: 600, fontSize: 14 }}>{dayNum}</span>
                <span className="d-flex align-items-center gap-1">
                  {day.planned && day.status !== 'vacation' && day.status !== 'holiday' && day.status !== 'sick' && (
                    <span
                      title="Geplanter Arbeitstag"
                      style={{ fontSize: 9, background: '#0d6efd', color: '#fff', borderRadius: 4, padding: '1px 4px' }}
                    >
                      geplant
                    </span>
                  )}
                  {isToday && (
                    <span style={{ fontSize: 9, background: '#6c757d', color: '#fff', borderRadius: 4, padding: '1px 4px' }}>
                      heute
                    </span>
                  )}
                </span>
              </div>
              {day.soll > 0 && (
                <div style={{ fontSize: 12, marginTop: 8 }}>
                  {day.status === 'future' ? (
                    <span style={{ opacity: 0.7 }}>Soll {day.soll}h</span>
                  ) : (
                    <span style={{ fontWeight: 600 }}>
                      {day.ist}
                      <span style={{ opacity: 0.6 }}> / {day.soll}h</span>
                    </span>
                  )}
                </div>
              )}
              {day.status === 'holiday' && day.holidayName && (
                <div style={{ fontSize: 10, marginTop: 6, lineHeight: 1.1 }}>{day.holidayName}</div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MonthGrid;
